import React from 'react';
import {
    Anchor,
    Box,
    Group,
    HoverCard,
    SimpleGrid,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { IconMapPin, IconPhone } from '@tabler/icons-react';

import type { Competitor } from '../../../codegen/graphql';
import stringifyNumber from '../../../utility/stringifyNumber';
import { PropagationStopper } from '../../common';
import {
    CELL_DETAIL_ARROW_CLASSNAME,
    CELL_DETAIL_DROPDOWN_CLASSNAME,
} from '../table-cell-detail/cellDetailSurfaceClasses';

type Props = {
    competitor: Competitor;
    children: React.ReactNode;
};

const LABEL_CLASS =
    'text-[10px] font-bold uppercase tracking-wide text-primary/70';

export function CompetitorDetailsHoverCard({ competitor, children }: Props) {
    const name = competitor.name?.trim() || 'N/A';
    const tel = stringifyNumber(competitor.telNo);
    const storeDisplay = competitor.address?.trim() || 'N/A';

    return (
        <HoverCard
            withArrow
            arrowOffset={6}
            arrowSize={10}
            arrowPosition="center"
            position="bottom"
            offset={10}
            withinPortal
            floatingStrategy="fixed"
            middlewares={{ flip: true, shift: true, size: true }}
            shadow="md"
            zIndex={400}
            openDelay={200}
            closeDelay={400}
            transitionProps={{
                transition: 'scale',
                duration: 200,
            }}
            classNames={{
                dropdown: CELL_DETAIL_DROPDOWN_CLASSNAME,
                arrow: CELL_DETAIL_ARROW_CLASSNAME,
            }}
        >
            <HoverCard.Target>
                <PropagationStopper className="flex min-w-0 w-full max-w-full">
                    {children}
                </PropagationStopper>
            </HoverCard.Target>
            <HoverCard.Dropdown>
                <Stack
                    gap={0}
                    className="max-h-[min(70vh,32rem)] overflow-y-auto overflow-x-hidden"
                >
                    <Box
                        px="md"
                        pt="md"
                        pb="sm"
                        className="border-b border-gray-100 shrink-0"
                    >
                        <Title order={5} className="text-gray-900 font-bold">
                            Competitor Details
                        </Title>
                    </Box>

                    <SimpleGrid
                        cols={{ base: 1, sm: 3 }}
                        spacing="lg"
                        verticalSpacing="md"
                        px="md"
                        py="md"
                    >
                        <Stack gap={6} className="min-w-0">
                            <Text className={LABEL_CLASS}>Name</Text>
                            <Text
                                size="sm"
                                className="text-gray-900 font-medium break-words"
                            >
                                {name}
                            </Text>
                        </Stack>
                        <Stack gap={6} className="min-w-0">
                            <Text className={LABEL_CLASS}>Contact details</Text>
                            <Group
                                gap={6}
                                wrap="nowrap"
                                align="flex-start"
                                className="min-w-0"
                            >
                                <IconPhone
                                    size={16}
                                    className="text-gray-400 shrink-0 mt-0.5"
                                    stroke={1.5}
                                    aria-hidden
                                />
                                {tel ? (
                                    <Anchor
                                        href={`tel:${tel}`}
                                        size="sm"
                                        className="break-all leading-snug"
                                        c="dark"
                                        underline="hover"
                                    >
                                        {tel}
                                    </Anchor>
                                ) : (
                                    <Text
                                        size="sm"
                                        className="text-gray-900 leading-snug"
                                    >
                                        N/A
                                    </Text>
                                )}
                            </Group>
                        </Stack>
                        <Stack gap={6} className="min-w-0">
                            <Text className={LABEL_CLASS}>Store</Text>
                            <Group
                                gap={6}
                                wrap="nowrap"
                                align="flex-start"
                                className="min-w-0"
                            >
                                <IconMapPin
                                    size={16}
                                    className="text-gray-400 shrink-0 mt-0.5"
                                    stroke={1.5}
                                    aria-hidden
                                />
                                <Text
                                    size="sm"
                                    className="text-gray-900 break-words leading-snug"
                                >
                                    {storeDisplay}
                                </Text>
                            </Group>
                        </Stack>
                    </SimpleGrid>
                </Stack>
            </HoverCard.Dropdown>
        </HoverCard>
    );
}
